import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SaveIcon from '@mui/icons-material/Save';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { ActionProps } from '../../contextProviders/AppProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useData } from '../../contextProviders/DataProvider';
import { useAutoSave } from '../../stateHooks/useAutoSave';
import Notice, {
  NoticeType,
  NoticeErrorLevel,
  NOTICE_STATUSES,
  NOTICE_TYPES,
  NOTICE_ERROR_LEVELS,
  NOTICE_CONTEXT_TYPES,
} from '../../types/Notice';
import { CombineDateJSTimeJS, ToDayJS } from '../../utils/utils';
import GratiSystemError from '../common/GratiSystemError';
import OrgSelector from '../org/OrgSelector';

export type NoticeEditorProps = {
  notice: Notice;
  isNew?: boolean;
  handleCreate?: (notice: Notice) => void;
  handleDelete?: (notice: Notice) => void;
  handleRestore?: (notice: Notice) => void;
  updateNotice?: (notice: Partial<Notice>) => Promise<void>;
  onActionsChange?: (pageActions: ActionProps[] | null) => void;
};

export default function NoticeEditor(props: NoticeEditorProps): ReactElement {
  const {
    isNew = false,
    handleCreate,
    handleDelete,
    handleRestore,
    updateNotice,
    onActionsChange,
  } = props;

  const { isGod } = useAuth();
  const { canCreateNotices, isOrgAdmin } = useData();

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [notice, setNotice] = useState<Notice>(props.notice);

  // Auto-save hook (only active for edit mode)
  const { queueChange } = useAutoSave<Notice>({
    updateFn: updateNotice,
    itemId: notice.itemId,
    additionalFields: { orgId: notice.orgId },
    enabled: !isNew && !!updateNotice,
  });

  // Handle field changes with auto-save for edit mode
  const handleFieldChange = <K extends keyof Notice>(field: K, value: Notice[K]) => {
    setNotice((prev) => ({ ...prev, [field]: value }));
    queueChange(field, value);
  };

  const noticeTypeOptions: { value: NoticeType; label: string }[] = [
    { value: NOTICE_TYPES.ANNOUNCEMENT, label: 'Announcement' },
    { value: NOTICE_TYPES.ALERT, label: 'Alert' },
    { value: NOTICE_TYPES.ERROR, label: 'Error' },
    { value: NOTICE_TYPES.FROSTDELAY, label: 'Frost Delay' },
    { value: NOTICE_TYPES.CALENDAR, label: 'Calendar' },
  ];

  const noticeErrorLevelOptions: { value: NoticeErrorLevel; label: string }[] = [
    { value: NOTICE_ERROR_LEVELS.INFO, label: 'Info' },
    { value: NOTICE_ERROR_LEVELS.WARNING, label: 'Warning' },
    { value: NOTICE_ERROR_LEVELS.ERROR, label: 'Error' },
    { value: NOTICE_ERROR_LEVELS.SUCCESS, label: 'Success' },
  ];

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleDeleteNotice = () => {
    if (handleDelete) {
      handleDelete(notice);
    }
    setDeleteConfirm(false);
  };

  const onSubmit = () => {
    if (!handleCreate) return;
    handleCreate(notice);
  };

  // Store callbacks in refs to avoid infinite loops in useEffect
  const onActionsChangeRef = useRef(onActionsChange);
  const handleRestoreRef = useRef(handleRestore);

  useEffect(() => {
    onActionsChangeRef.current = onActionsChange;
    handleRestoreRef.current = handleRestore;
  });

  // Build page actions - only depend on the specific fields we need
  const noticeTitle = notice.title;
  const noticeText = notice.text;
  const noticeOrgId = notice.orgId;
  const noticeStatus = notice.status;

  useEffect(() => {
    if (!onActionsChangeRef.current) return;

    const pageActions: ActionProps[] | null = isNew ? [
      { name: 'Create', icon: SaveIcon, type: 'submit', form: 'notice-editor-form', disabled: !noticeTitle.trim() || !noticeText.trim() || !noticeOrgId, variant: 'contained', color: 'primary' },
    ] : isGod && noticeStatus === NOTICE_STATUSES.DELETED && handleRestoreRef.current ? [
      { name: 'Restore', icon: RestoreFromTrashIcon, handleClick: () => handleRestoreRef.current?.(notice), variant: 'text', color: 'secondary' },
    ] : null;

    onActionsChangeRef.current(pageActions);
  }, [noticeTitle, noticeText, noticeOrgId, noticeStatus, isNew, isGod, notice]);

  // Handle org selection change for new notices
  const handleOrgChange = useCallback((orgPair: { orgId: string; orgName: string }) => {
    setNotice((prev) => ({
      ...prev,
      orgId: orgPair.orgId,
      orgName: orgPair.orgName,
      contextPairs: [{
        id: orgPair.orgId,
        name: orgPair.orgName,
        type: NOTICE_CONTEXT_TYPES.ORG,
      }],
    }));
  }, []);

  // Authorization check: for new notices, user must be admin of at least one org
  // For existing notices, user must be admin of that specific org
  const isAuthorized = (isNew && canCreateNotices()) || (notice.orgId && isOrgAdmin(notice.orgId));

  if (notice) {
    if (isAuthorized) {
      return (
        <Box
          id="notice-editor-form"
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          sx={{ py: 2 }}
        >
          <Stack spacing={2}>
            {isNew ? (
              <OrgSelector
                value={notice.orgId}
                onChange={handleOrgChange}
              />
            ) : (
              <TextField
                id="org"
                label="Org"
                value={notice.orgName}
                disabled
              />
            )}

            <FormControl fullWidth>
              <InputLabel id="notice-type-label">Notice type</InputLabel>
              <Select
                labelId="notice-type-label"
                id="notice-type"
                value={notice.type}
                label="Notice type"
                onChange={(e) => handleFieldChange('type', e.target.value as NoticeType)}
              >
                {noticeTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="notice-error-level-label">Error level</InputLabel>
              <Select
                labelId="notice-error-level-label"
                id="notice-error-level"
                value={notice.errorLevel || NOTICE_ERROR_LEVELS.INFO}
                label="Error level"
                onChange={(e) => handleFieldChange('errorLevel', e.target.value as NoticeErrorLevel)}
              >
                {noticeErrorLevelOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              id="title"
              label="Title"
              aria-label="Title"
              required={isNew}
              value={notice.title}
              onChange={({ target }) => handleFieldChange('title', target.value)}
            />

            <TextField
              id="text"
              label="Details"
              aria-label="Details"
              required={isNew}
              multiline
              rows={4}
              value={notice.text}
              onChange={({ target }) => handleFieldChange('text', target.value)}
            />

            <TextField
              id="link"
              label="Link (optional)"
              aria-label="Link"
              value={notice.link}
              onChange={({ target }) => handleFieldChange('link', target.value)}
            />

            <Stack spacing={2}>
              <Typography variant="titleMedium">Display timeframe</Typography>

              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                <DatePicker
                  label="Begin date"
                  value={ToDayJS(notice.displayStartsAt as Timestamp, timezone)}
                  onChange={(newValue) => {
                    if (newValue) {
                      const currentTime = ToDayJS(
                        notice.displayStartsAt as Timestamp,
                        timezone
                      );
                      const newDateTime = CombineDateJSTimeJS(newValue, currentTime);
                      handleFieldChange('displayStartsAt', newDateTime);
                    }
                  }}
                />
                <TimePicker
                  label="Begin time"
                  value={ToDayJS(notice.displayStartsAt as Timestamp, timezone)}
                  onChange={(newValue) => {
                    if (newValue) {
                      const currentDate = ToDayJS(notice.displayStartsAt as Timestamp, timezone);
                      const newDateTime = CombineDateJSTimeJS(currentDate, newValue);
                      handleFieldChange('displayStartsAt', newDateTime);
                    }
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                <DatePicker
                  label="End date"
                  value={ToDayJS(notice.displayStopsAt as Timestamp, timezone)}
                  onChange={(newValue) => {
                    if (newValue) {
                      const currentTime = ToDayJS(notice.displayStopsAt as Timestamp, timezone);
                      const newDateTime = CombineDateJSTimeJS(newValue, currentTime);
                      handleFieldChange('displayStopsAt', newDateTime);
                    }
                  }}
                />
                <TimePicker
                  label="End time"
                  value={ToDayJS(notice.displayStopsAt as Timestamp, timezone)}
                  onChange={(newValue) => {
                    if (newValue) {
                      const currentDate = ToDayJS(notice.displayStopsAt as Timestamp, timezone);
                      const newDateTime = CombineDateJSTimeJS(currentDate, newValue);
                      handleFieldChange('displayStopsAt', newDateTime);
                    }
                  }}
                />
              </Stack>
            </Stack>

            {/* Delete action for existing notices */}
            {!isNew && handleDelete && notice.status !== NOTICE_STATUSES.DELETED && (
              <Stack direction="row" spacing={2} sx={{
                alignItems: "center"
              }}>
                {!deleteConfirm && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                )}
                {deleteConfirm && (
                  <>
                    <Button size="small" color="error" onClick={handleDeleteNotice}>
                      Confirm Delete
                    </Button>
                    <Button size="small" onClick={() => setDeleteConfirm(false)}>
                      Cancel
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      );
    } else {
      return <GratiSystemError error={new Error('Not authorized to edit this notice')} />;
    }
  }

  return <></>;
}
